import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Search, Loader2, UserX, Plus, Trash2, ShieldOff } from 'lucide-react';
import toast from 'react-hot-toast';
import {
    fetchAnalyticsExclusions,
    addAnalyticsExclusion,
    removeAnalyticsExclusion,
    updateAnalyticsExclusionScope,
    searchExclusionCandidates,
} from '../api/analyticsApi';

/**
 * Modal to manage accounts excluded from analytics.
 *
 * Each exclusion is scoped, because the two environments measure opposite
 * things: Portal Analytics measures buyer behaviour (so staff logins are
 * excluded), Agent Analytics measures agent performance (so an agent must stay
 * visible even though their portal login is excluded). `onChanged` is called
 * after any add/remove/scope change so the parent can refresh its data.
 */
const SCOPES = [
    {
        field: 'exclude_from_portal',
        label: 'Portal',
        onHint: 'Hidden from Portal Analytics. Click to include again.',
        offHint: 'Counted in Portal Analytics. Click to hide.',
    },
    {
        field: 'exclude_from_crm',
        label: 'CRM',
        onHint: 'Hidden from Agent Analytics (Leaderboard, Agents, Agent Usage). Click to include again.',
        offHint: 'Counted in Agent Analytics. Click to hide.',
    },
];

const AnalyticsExclusionsModal = ({ isOpen, onClose, onChanged }) => {
    const [exclusions, setExclusions] = useState([]);
    const [loadingList, setLoadingList] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const searchTimer = useRef(null);

    const loadExclusions = useCallback(async () => {
        try {
            setLoadingList(true);
            const data = await fetchAnalyticsExclusions();
            setExclusions(Array.isArray(data) ? data : []);
        } catch (err) {
            toast.error('Failed to load excluded accounts');
        } finally {
            setLoadingList(false);
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            loadExclusions();
            setSearchTerm('');
            setSearchResults([]);
        }
    }, [isOpen, loadExclusions]);

    // Debounced account search (all roles — buyers, agents, admins)
    useEffect(() => {
        if (!isOpen) return;
        if (searchTimer.current) clearTimeout(searchTimer.current);

        const term = searchTerm.trim();
        if (term.length < 2) {
            setSearchResults([]);
            setSearching(false);
            return;
        }

        setSearching(true);
        searchTimer.current = setTimeout(async () => {
            try {
                const results = await searchExclusionCandidates(term);
                setSearchResults(Array.isArray(results) ? results : []);
            } catch (err) {
                console.error('Account search failed:', err);
                setSearchResults([]);
            } finally {
                setSearching(false);
            }
        }, 350);

        return () => clearTimeout(searchTimer.current);
    }, [searchTerm, isOpen]);

    const excludedIds = new Set(exclusions.map((e) => e.buyer_id));

    const handleAdd = async (buyer) => {
        setBusyId(buyer.id);
        try {
            await addAnalyticsExclusion(buyer.id);
            toast.success(`${buyer.name || buyer.email} excluded from analytics`);
            await loadExclusions();
            onChanged?.();
        } catch (err) {
            toast.error('Failed to exclude account');
        } finally {
            setBusyId(null);
        }
    };

    const handleToggleScope = async (exclusion, field) => {
        setBusyId(exclusion.id);
        try {
            await updateAnalyticsExclusionScope(exclusion.id, {
                [field]: !exclusion[field],
            });
            await loadExclusions();
            onChanged?.();
        } catch (err) {
            toast.error('Failed to update exclusion');
        } finally {
            setBusyId(null);
        }
    };

    const handleRemove = async (exclusion) => {
        setBusyId(exclusion.id);
        try {
            await removeAnalyticsExclusion(exclusion.id);
            toast.success(`${exclusion.name || exclusion.email} re-included`);
            await loadExclusions();
            onChanged?.();
        } catch (err) {
            toast.error('Failed to remove exclusion');
        } finally {
            setBusyId(null);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-rose-50 rounded-lg">
                            <ShieldOff className="w-5 h-5 text-rose-600" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-gray-900">Analytics Exclusions</h3>
                            <p className="text-xs text-gray-500 mt-0.5">
                                Choose which analytics each account is hidden from
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        aria-label="Close"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-5 overflow-y-auto space-y-5">
                    {/* Search + add */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                            Add an account to exclude
                        </label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search by name, email or phone…"
                                className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            />
                            {searching && (
                                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
                            )}
                        </div>

                        {/* Search results */}
                        {searchResults.length > 0 && (
                            <div className="mt-2 border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-56 overflow-y-auto">
                                {searchResults.map((buyer) => {
                                    const already = buyer.already_excluded || excludedIds.has(buyer.id);
                                    return (
                                        <div
                                            key={buyer.id}
                                            className="flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-gray-50"
                                        >
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-gray-900 truncate flex items-center gap-1.5">
                                                    {buyer.name || '—'}
                                                    {buyer.role && buyer.role !== 'buyer' && (
                                                        <span className="px-1.5 py-0 rounded text-[10px] font-semibold uppercase bg-amber-100 text-amber-700 flex-shrink-0">
                                                            {buyer.role}
                                                        </span>
                                                    )}
                                                </p>
                                                <p className="text-xs text-gray-500 truncate">{buyer.email}</p>
                                            </div>
                                            {already ? (
                                                <span className="text-xs font-medium text-gray-400 flex-shrink-0">
                                                    Excluded
                                                </span>
                                            ) : (
                                                <button
                                                    onClick={() => handleAdd(buyer)}
                                                    disabled={busyId === buyer.id}
                                                    className="flex items-center gap-1 px-2.5 py-1.5 bg-rose-600 text-white text-xs font-semibold rounded-lg hover:bg-rose-700 transition-colors disabled:opacity-50 flex-shrink-0"
                                                >
                                                    {busyId === buyer.id ? (
                                                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                    ) : (
                                                        <Plus className="w-3.5 h-3.5" />
                                                    )}
                                                    Exclude
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        {searchTerm.trim().length >= 2 && !searching && searchResults.length === 0 && (
                            <p className="mt-2 text-xs text-gray-400">No accounts match “{searchTerm}”.</p>
                        )}
                    </div>

                    {/* Current exclusions */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                                Excluded accounts
                            </h4>
                            <span className="text-xs font-medium text-gray-400">
                                {exclusions.length} total
                            </span>
                        </div>

                        <p className="text-xs text-gray-500 mb-2 leading-relaxed">
                            <span className="font-semibold text-gray-600">Portal</span> hides the
                            account from buyer/website analytics.{' '}
                            <span className="font-semibold text-gray-600">CRM</span> also removes
                            them from the Leaderboard, Agents and Agent Usage tables — only tick
                            it for people who should never be reported on as an agent.
                        </p>

                        {loadingList ? (
                            <div className="flex items-center justify-center py-8 text-gray-400">
                                <Loader2 className="w-5 h-5 animate-spin" />
                            </div>
                        ) : exclusions.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-8 text-center">
                                <UserX className="w-8 h-8 text-gray-300 mb-2" />
                                <p className="text-sm text-gray-500">No accounts are excluded yet.</p>
                                <p className="text-xs text-gray-400 mt-0.5">
                                    Search above to exclude internal or test accounts.
                                </p>
                            </div>
                        ) : (
                            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                                {exclusions.map((exc) => (
                                    <div
                                        key={exc.id}
                                        className="flex items-center justify-between gap-3 px-3 py-2.5"
                                    >
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium text-gray-900 truncate flex items-center gap-1.5">
                                                {exc.name || '—'}
                                                {exc.role && exc.role !== 'buyer' && (
                                                    <span className="px-1.5 py-0 rounded text-[10px] font-semibold uppercase bg-amber-100 text-amber-700 flex-shrink-0">
                                                        {exc.role}
                                                    </span>
                                                )}
                                            </p>
                                            <p className="text-xs text-gray-500 truncate">{exc.email}</p>
                                        </div>
                                        <div className="flex items-center gap-1.5 flex-shrink-0">
                                            {SCOPES.map((scope) => {
                                                const on = exc[scope.field];
                                                return (
                                                    <button
                                                        key={scope.field}
                                                        onClick={() => handleToggleScope(exc, scope.field)}
                                                        disabled={busyId === exc.id}
                                                        title={on ? scope.onHint : scope.offHint}
                                                        className={`px-2 py-1 rounded-md text-[11px] font-semibold border transition-colors disabled:opacity-50 ${
                                                            on
                                                                ? 'bg-rose-50 border-rose-200 text-rose-700 hover:bg-rose-100'
                                                                : 'bg-white border-gray-200 text-gray-400 hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        {scope.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <button
                                            onClick={() => handleRemove(exc)}
                                            disabled={busyId === exc.id}
                                            className="flex items-center gap-1 px-2.5 py-1.5 text-rose-600 text-xs font-semibold rounded-lg hover:bg-rose-50 transition-colors disabled:opacity-50 flex-shrink-0"
                                        >
                                            {busyId === exc.id ? (
                                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                            ) : (
                                                <Trash2 className="w-3.5 h-3.5" />
                                            )}
                                            Remove
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
                    <button
                        onClick={onClose}
                        className="px-5 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                    >
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AnalyticsExclusionsModal;
